/**
 * Mob renderer — box-based 3D models for each mob type.
 * Zombie/Skeleton: humanoid (head + body + arms + legs)
 * Spider: wide body + legs
 */

import * as THREE from "three";
import { MobType, type MobEntity, type MobTypeValue } from "../systems/mob-system";

// ─── Color palettes per mob ───

const COLORS = {
  [MobType.ZOMBIE]: {
    head: 0x4a7a3a,
    body: 0x3a6a2a,
    arms: 0x4a7a3a,
    legs: 0x2a4a8a,
    eyes: 0xff0000,
  },
  [MobType.SKELETON]: {
    head: 0xcccccc,
    body: 0xbbbbbb,
    arms: 0xaaaaaa,
    legs: 0xaaaaaa,
    eyes: 0x222222,
  },
  [MobType.SPIDER]: {
    body: 0x3a2a1a,
    head: 0x2a1a0a,
    legs: 0x4a3a2a,
    eyes: 0xff0000,
  },
};

// Passive animals: body/head/legs/detail colors
const ANIMAL_COLORS: Record<number, { body: number; head: number; legs: number; snout: number }> = {
  [MobType.PIG]: { body: 0xf0a0a8, head: 0xf0a0a8, legs: 0xe89098, snout: 0xd87880 },
  [MobType.COW]: { body: 0x5a4030, head: 0x4a3020, legs: 0x3a2818, snout: 0xd8c8b8 },
  [MobType.CHICKEN]: { body: 0xf0f0e8, head: 0xffffff, legs: 0xd8a020, snout: 0xe83028 },
};

// ─── Model builders ───

function createHumanoidModel(type: typeof MobType.ZOMBIE | typeof MobType.SKELETON): THREE.Group {
  const group = new THREE.Group();
  const c = COLORS[type];
  const thin = type === MobType.SKELETON;

  const bodyW = thin ? 0.35 : 0.45;
  const bodyH = 0.6;
  const bodyD = thin ? 0.2 : 0.25;

  // Body
  const bodyGeo = new THREE.BoxGeometry(bodyW, bodyH, bodyD);
  const bodyMat = new THREE.MeshLambertMaterial({ color: c.body });
  const body = new THREE.Mesh(bodyGeo, bodyMat);
  body.position.y = 0.9;
  group.add(body);

  // Head
  const headSize = thin ? 0.32 : 0.38;
  const headGeo = new THREE.BoxGeometry(headSize, headSize, headSize);
  const headMat = new THREE.MeshLambertMaterial({ color: c.head });
  const head = new THREE.Mesh(headGeo, headMat);
  head.position.y = 1.4;
  group.add(head);

  // Eyes (two small red cubes)
  const eyeSize = 0.05;
  const eyeGeo = new THREE.BoxGeometry(eyeSize, eyeSize, eyeSize);
  const eyeMat = new THREE.MeshBasicMaterial({ color: c.eyes });
  const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
  leftEye.position.set(-0.08, 1.42, -headSize / 2 - 0.01);
  group.add(leftEye);
  const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
  rightEye.position.set(0.08, 1.42, -headSize / 2 - 0.01);
  group.add(rightEye);

  // Arms
  const armW = thin ? 0.1 : 0.15;
  const armH = 0.55;
  const armGeo = new THREE.BoxGeometry(armW, armH, armW);
  const armMat = new THREE.MeshLambertMaterial({ color: c.arms });

  const leftArm = new THREE.Mesh(armGeo, armMat);
  leftArm.position.set(-bodyW / 2 - armW / 2, 0.9, 0);
  leftArm.name = "leftArm";
  group.add(leftArm);

  const rightArm = new THREE.Mesh(armGeo, armMat);
  rightArm.position.set(bodyW / 2 + armW / 2, 0.9, 0);
  rightArm.name = "rightArm";
  group.add(rightArm);

  // Legs
  const legW = thin ? 0.1 : 0.15;
  const legH = 0.55;
  const legGeo = new THREE.BoxGeometry(legW, legH, legW);
  const legMat = new THREE.MeshLambertMaterial({ color: c.legs });

  const leftLeg = new THREE.Mesh(legGeo, legMat);
  leftLeg.position.set(-0.1, 0.28, 0);
  leftLeg.name = "leftLeg";
  group.add(leftLeg);

  const rightLeg = new THREE.Mesh(legGeo, legMat);
  rightLeg.position.set(0.1, 0.28, 0);
  rightLeg.name = "rightLeg";
  group.add(rightLeg);

  return group;
}

function createSpiderModel(): THREE.Group {
  const group = new THREE.Group();
  const c = COLORS[MobType.SPIDER];

  // Body (wide, flat)
  const bodyGeo = new THREE.BoxGeometry(0.7, 0.35, 0.5);
  const bodyMat = new THREE.MeshLambertMaterial({ color: c.body });
  const body = new THREE.Mesh(bodyGeo, bodyMat);
  body.position.y = 0.35;
  group.add(body);

  // Head (front, smaller)
  const headGeo = new THREE.BoxGeometry(0.35, 0.3, 0.35);
  const headMat = new THREE.MeshLambertMaterial({ color: c.head });
  const head = new THREE.Mesh(headGeo, headMat);
  head.position.set(0, 0.35, -0.4);
  group.add(head);

  // Eyes (8 small red dots — 4 per side)
  const eyeGeo = new THREE.BoxGeometry(0.04, 0.04, 0.04);
  const eyeMat = new THREE.MeshBasicMaterial({ color: c.eyes });
  for (let i = 0; i < 4; i++) {
    const xOff = (i % 2 === 0 ? -0.06 : 0.06) + (i < 2 ? -0.04 : 0.04);
    const yOff = i < 2 ? 0.04 : -0.02;
    const eye = new THREE.Mesh(eyeGeo, eyeMat);
    eye.position.set(xOff, 0.38 + yOff, -0.58);
    group.add(eye);
  }

  // Legs (4 pairs, angled)
  const legGeo = new THREE.BoxGeometry(0.06, 0.06, 0.5);
  const legMat = new THREE.MeshLambertMaterial({ color: c.legs });

  const legAngles = [-0.6, -0.2, 0.2, 0.6];
  for (let i = 0; i < 4; i++) {
    // Left
    const ll = new THREE.Mesh(legGeo, legMat);
    ll.position.set(-0.45, 0.2, legAngles[i] * 0.4);
    ll.rotation.z = -0.6;
    ll.rotation.y = legAngles[i] * 0.3;
    group.add(ll);

    // Right
    const rl = new THREE.Mesh(legGeo, legMat);
    rl.position.set(0.45, 0.2, legAngles[i] * 0.4);
    rl.rotation.z = 0.6;
    rl.rotation.y = -legAngles[i] * 0.3;
    group.add(rl);
  }

  return group;
}

function createQuadrupedModel(type: typeof MobType.PIG | typeof MobType.COW): THREE.Group {
  const group = new THREE.Group();
  const c = ANIMAL_COLORS[type];
  const isCow = type === MobType.COW;
  const bodyW = isCow ? 0.65 : 0.55;
  const bodyH = isCow ? 0.55 : 0.45;
  const bodyL = isCow ? 0.95 : 0.8;
  const legH = isCow ? 0.45 : 0.3;

  const body = new THREE.Mesh(
    new THREE.BoxGeometry(bodyW, bodyH, bodyL),
    new THREE.MeshLambertMaterial({ color: c.body })
  );
  body.position.y = legH + bodyH / 2;
  group.add(body);

  const headSize = isCow ? 0.4 : 0.35;
  const head = new THREE.Mesh(
    new THREE.BoxGeometry(headSize, headSize, headSize * 0.8),
    new THREE.MeshLambertMaterial({ color: c.head })
  );
  head.position.set(0, legH + bodyH + headSize * 0.1, -bodyL / 2 - headSize * 0.2);
  group.add(head);

  // Snout / muzzle
  const snout = new THREE.Mesh(
    new THREE.BoxGeometry(headSize * 0.5, headSize * 0.3, 0.08),
    new THREE.MeshLambertMaterial({ color: c.snout })
  );
  snout.position.set(0, head.position.y - headSize * 0.15, head.position.z - headSize * 0.45);
  group.add(snout);

  // 4 legs
  const legGeo = new THREE.BoxGeometry(0.16, legH, 0.16);
  const legMat = new THREE.MeshLambertMaterial({ color: c.legs });
  const legOffsets: Array<[number, number]> = [
    [-bodyW / 2 + 0.1, -bodyL / 2 + 0.12],
    [bodyW / 2 - 0.1, -bodyL / 2 + 0.12],
    [-bodyW / 2 + 0.1, bodyL / 2 - 0.12],
    [bodyW / 2 - 0.1, bodyL / 2 - 0.12],
  ];
  for (const [lx, lz] of legOffsets) {
    const leg = new THREE.Mesh(legGeo, legMat);
    leg.position.set(lx, legH / 2, lz);
    group.add(leg);
  }

  return group;
}

function createChickenModel(): THREE.Group {
  const group = new THREE.Group();
  const c = ANIMAL_COLORS[MobType.CHICKEN];

  const body = new THREE.Mesh(
    new THREE.BoxGeometry(0.3, 0.3, 0.4),
    new THREE.MeshLambertMaterial({ color: c.body })
  );
  body.position.y = 0.35;
  group.add(body);

  const head = new THREE.Mesh(
    new THREE.BoxGeometry(0.18, 0.22, 0.18),
    new THREE.MeshLambertMaterial({ color: c.head })
  );
  head.position.set(0, 0.62, -0.18);
  group.add(head);

  // Beak + comb
  const beak = new THREE.Mesh(
    new THREE.BoxGeometry(0.1, 0.06, 0.08),
    new THREE.MeshLambertMaterial({ color: 0xe8a020 })
  );
  beak.position.set(0, 0.6, -0.32);
  group.add(beak);

  const comb = new THREE.Mesh(
    new THREE.BoxGeometry(0.06, 0.08, 0.1),
    new THREE.MeshLambertMaterial({ color: c.snout })
  );
  comb.position.set(0, 0.76, -0.18);
  group.add(comb);

  // Legs
  const legGeo = new THREE.BoxGeometry(0.05, 0.2, 0.05);
  const legMat = new THREE.MeshLambertMaterial({ color: c.legs });
  for (const lx of [-0.07, 0.07]) {
    const leg = new THREE.Mesh(legGeo, legMat);
    leg.position.set(lx, 0.1, 0);
    group.add(leg);
  }

  return group;
}

// ─── Health bar ───

function createHealthBar(): THREE.Group {
  const group = new THREE.Group();

  // Background
  const bgGeo = new THREE.PlaneGeometry(0.8, 0.08);
  const bgMat = new THREE.MeshBasicMaterial({ color: 0x333333, side: THREE.DoubleSide });
  const bg = new THREE.Mesh(bgGeo, bgMat);
  group.add(bg);

  // Fill
  const fillGeo = new THREE.PlaneGeometry(0.76, 0.06);
  const fillMat = new THREE.MeshBasicMaterial({ color: 0xff3333, side: THREE.DoubleSide });
  const fill = new THREE.Mesh(fillGeo, fillMat);
  fill.position.z = 0.001;
  fill.name = "healthFill";
  group.add(fill);

  return group;
}

// ─── Mob Renderer System ───

type MobVisual = {
  model: THREE.Group;
  healthBar: THREE.Group;
  mobId: number;
};

export type MobRenderer = {
  syncMobs: (mobs: MobEntity[], camera: THREE.Camera) => void;
  dispose: () => void;
};

export function createMobRenderer(scene: THREE.Scene): MobRenderer {
  const visuals = new Map<number, MobVisual>();

  function getOrCreateVisual(mob: MobEntity): MobVisual {
    let vis = visuals.get(mob.id);
    if (vis) return vis;

    let model: THREE.Group;
    if (mob.type === MobType.SPIDER) {
      model = createSpiderModel();
    } else if (mob.type === MobType.PIG || mob.type === MobType.COW) {
      model = createQuadrupedModel(mob.type);
    } else if (mob.type === MobType.CHICKEN) {
      model = createChickenModel();
    } else {
      model = createHumanoidModel(mob.type);
    }

    const healthBar = createHealthBar();
    const hbHeight =
      mob.type === MobType.SPIDER ? 1.0 :
      mob.type === MobType.CHICKEN ? 0.95 :
      mob.type === MobType.PIG ? 1.2 :
      mob.type === MobType.COW ? 1.6 : 2.0;
    healthBar.position.y = hbHeight;
    model.add(healthBar);

    scene.add(model);
    vis = { model, healthBar, mobId: mob.id };
    visuals.set(mob.id, vis);
    return vis;
  }

  function syncMobs(mobs: MobEntity[], camera: THREE.Camera) {
    const activeMobIds = new Set<number>();

    for (const mob of mobs) {
      activeMobIds.add(mob.id);
      const vis = getOrCreateVisual(mob);

      // Position
      vis.model.position.set(mob.x, mob.y, mob.z);
      vis.model.rotation.y = mob.yaw;

      // Death animation — shrink + fall
      if (mob.dead) {
        const deathProgress = Math.min(mob.deathTimer / 0.8, 1);
        vis.model.scale.y = 1 - deathProgress * 0.8;
        vis.model.rotation.z = deathProgress * 1.5;
        vis.model.position.y = mob.y - deathProgress * 0.5;
      } else {
        vis.model.scale.y = 1;
        vis.model.rotation.z = 0;
      }

      // Walk animation (arm/leg swing)
      const speed = Math.sqrt(mob.vx * mob.vx + mob.vz * mob.vz);
      if (speed > 0.1 && !mob.dead && mob.type !== MobType.SPIDER) {
        const swing = Math.sin(Date.now() * 0.008) * 0.6;
        const leftArm = vis.model.getObjectByName("leftArm") as THREE.Mesh;
        const rightArm = vis.model.getObjectByName("rightArm") as THREE.Mesh;
        const leftLeg = vis.model.getObjectByName("leftLeg") as THREE.Mesh;
        const rightLeg = vis.model.getObjectByName("rightLeg") as THREE.Mesh;
        if (leftArm) leftArm.rotation.x = swing;
        if (rightArm) rightArm.rotation.x = -swing;
        if (leftLeg) leftLeg.rotation.x = -swing;
        if (rightLeg) rightLeg.rotation.x = swing;
      }

      // Health bar: face camera + scale
      vis.healthBar.lookAt(camera.position);
      const healthFrac = mob.health / mob.maxHealth;
      const fill = vis.healthBar.getObjectByName("healthFill") as THREE.Mesh;
      if (fill) {
        fill.scale.x = Math.max(0.01, healthFrac);
        fill.position.x = -(1 - healthFrac) * 0.38;
        (fill.material as THREE.MeshBasicMaterial).color.setHex(
          healthFrac > 0.5 ? 0x44cc44 : healthFrac > 0.25 ? 0xcccc44 : 0xff3333
        );
      }

      // Hide health bar at full health
      vis.healthBar.visible = mob.health < mob.maxHealth;
    }

    // Remove visuals for despawned mobs
    for (const [id, vis] of visuals) {
      if (!activeMobIds.has(id)) {
        scene.remove(vis.model);
        disposeGroup(vis.model);
        visuals.delete(id);
      }
    }
  }

  function disposeGroup(group: THREE.Group) {
    group.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        if (Array.isArray(obj.material)) {
          obj.material.forEach((m) => m.dispose());
        } else {
          obj.material.dispose();
        }
      }
    });
  }

  function dispose() {
    for (const [_, vis] of visuals) {
      scene.remove(vis.model);
      disposeGroup(vis.model);
    }
    visuals.clear();
  }

  return { syncMobs, dispose };
}
